import { useEffect, useRef, useState } from "react";
import subscriptionService, { ISubscription, SubscriptionType } from "../../services/SubscriptionService";
import { useLocation } from "react-router-dom";
import { DefaultButton, Separator, Spinner, SpinnerSize } from "@fluentui/react";
import { ContractsUnderSameSubscription } from "./RePaymentSubscription";
import DocumentTitle from "react-document-title";
import Auth from "../../Auth/Auth";
import UsersSubscription, { TRenewalOptionsDTO, TSubscriptionOptionsDTO } from "../../services/UsersSubscriptionService";
import { SuscripcionSelector } from "./SuscripcionSelector/SuscripcionSelector";
import { useHistory } from "react-router-dom";
import { DEFAULT_PLAN } from "./SuscripcionSelector/util";
import { resolveRenewalPreselectedId, sortOptionsForChart, toSubscriptionFromOption } from "./subscriptionOptionsRules";
import { useTrackEvent } from "@microsoft/applicationinsights-react-js";
import { ai } from "@/services/TelemetryService";
import React from "react";

export const MoreResuscriptionPlans = () =>
{
    const { state } = useLocation<any>();
    const contracts = useRef<ContractsUnderSameSubscription>(state?.contracts);
    const prevSubscription = contracts.current?.subscriptionId;
    const limitAmount = contracts.current?.subscriptionLimit;
    const isLoading = useRef<boolean>(false);
    const [subscriptions, setSubscriptions] = useState<SubscriptionType[]>();
    const [portfolioValue, setPortfolioValue] = useState<number>(0);
    const [selectedPlan, setSelectedPlan] = useState<ISubscription>(DEFAULT_PLAN);
    // Opciones de renovación del backend (única fuente de las reglas de tarifa).
    const [renewal, setRenewal] = useState<TRenewalOptionsDTO>();
    const [loadError, setLoadError] = useState<string>();

    useEffect(() =>
    {
        const fetchData = async () =>
        {
            if(subscriptions === undefined)
            {
                try
                {
                    const userSubscriptionId = contracts.current?.userSubscriptionId;
                    if(!userSubscriptionId)
                    {
                        setLoadError("No se han recibido los datos de tu suscripción. Vuelve a Mis Planes y selecciona de nuevo la opción de renovación.");
                        isLoading.current = true;
                        setSubscriptions([]);
                        return;
                    }

                    // Opciones, tarifa requerida y patrimonio salen del backend (no del localStorage).
                    const response: TSubscriptionOptionsDTO = await UsersSubscription.getSubscriptionOptions(userSubscriptionId);
                    const renewalOptions = response?.renewal;
                    const options = sortOptionsForChart(renewalOptions?.options ?? []);

                    // La descripción no viene en el endpoint: se completa con el catálogo solo para mostrarla.
                    const types = Array.from(new Set(options.map((option) => option.type).filter((type) => !!type)));
                    const catalogs = await Promise.all(types.map((type) => subscriptionService.getSubscriptionsByType(type).catch(() => [] as ISubscription[])));
                    const descriptions = new Map<number, string>();
                    ([] as ISubscription[]).concat(...catalogs).forEach((sub) =>
                    {
                        if(sub?.id !== undefined)
                        {
                            descriptions.set(sub.id, sub.description);
                        }
                    });

                    const plans = options.map((option) => toSubscriptionFromOption(option, descriptions.get(option.id)));
                    const preselectedId = resolveRenewalPreselectedId(renewalOptions, state?.newSubscriptionId, response?.currentSubscription?.id ?? prevSubscription);
                    const preselected = plans.find((plan) => plan.id === preselectedId);
                    if(preselected)
                    {
                        setSelectedPlan(preselected);
                    }

                    setPortfolioValue(Number(response?.portfolioValue ?? 0) || 0);
                    setRenewal(renewalOptions);
                    isLoading.current = true;
                    setSubscriptions(plans);
                }
                catch(error: any)
                {
                    console.error("Error al cargar los datos:", error);
                    const status: number | undefined = error?.response?.status;
                    setLoadError(status === 401 || status === 403 || status === 404
                        ? "No se ha encontrado la suscripción que quieres renovar o no pertenece a tu usuario."
                        : "No se han podido obtener las opciones de renovación. Por favor, inténtelo de nuevo más tarde.");
                    isLoading.current = true;
                    setSubscriptions([]);
                }
            }
        }

        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevSubscription, limitAmount]);

    const appInsights = ai.reactPlugin;
    const history = useHistory();

    const trackClicks = useTrackEvent(appInsights, selectedPlan.name, selectedPlan.name);
    const handleContinuar = () =>
    {
        if(!selectedPlan || renewal?.allowed !== true)
        {
            return;
        }

        Auth.setRepaymentSubscription(selectedPlan.id);

        trackClicks(selectedPlan.name);

        history.push(
        {
            pathname: "/Repayment",
            state:
            {
                contracts: contracts.current,
                fromMorePlans: true,
                newSubscriptionId: selectedPlan.id,
            },
        });
    };
    
    if(isLoading.current === false)
    {
        return (<Spinner size={SpinnerSize.large} />);
    }
    else
    {
        return(
            <DocumentTitle title = 'IronIA - Renovación de Suscripción'>
                <React.Fragment key="more-resuscription-plans">
                    <h2>Renovación de Suscripción</h2>
                    <Separator />
                    <div className="shadow">
                        {loadError !== undefined &&
                        (
                            <p>{loadError}</p>
                        )}
                        {loadError === undefined && renewal?.allowed !== true &&
                        (
                            <p>{renewal?.notAllowedReason || "No es posible renovar tu suscripción."}</p>
                        )}
                        {loadError === undefined && renewal?.allowed === true && (subscriptions ?? []).length === 0 &&
                        (
                            <p>No hay suscripciones disponibles para renovar con el valor actual de tu cartera.</p>
                        )}
                        {loadError === undefined && renewal?.allowed === true && !!renewal.message &&
                        (
                            <p style={{fontWeight: "bold"}}>{renewal.message}</p>
                        )}
                        {renewal?.allowed === true && (subscriptions ?? []).length > 0 &&
                        (
                            <SuscripcionSelector
                                subscriptions={subscriptions ?? []}
                                selectedPlan={selectedPlan}
                                currentAmount={portfolioValue}
                                setSelectedPlan={setSelectedPlan}
                            />
                        )}
                        <div className="suscriptionButton__container">
                            <DefaultButton className="button tiny-primary-button"
                                text="Continuar"
                                disabled={renewal?.allowed !== true || (subscriptions ?? []).length === 0 || !selectedPlan?.id
                                    || !(subscriptions ?? []).some((sub) => sub.id === selectedPlan.id)}
                                onClick={handleContinuar}
                            />
                        </div>
                    </div>
                </React.Fragment>
            </DocumentTitle>
        );
    }
}